import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { NoteTone } from "@shared/ui";
import { ViewIcon, LinkIcon, LockIcon } from "@medentic/polaris-icons";
import type { IconProps } from "@medentic/polaris-react";
import {
  EventCategory,
  Visibility,
  TicketStatus,
  EventStatus,
  Format,
  ExpertiseLevel,
  InstructorTitle,
  PaymentPayout,
  PaymentProcessedPlatform,
  BrandRole,
  SocialProfileConfig,
  PrivateNoteTone,
  EventRelationType,
} from "@features/events/types";

// Icon mapping for visibility options
const VISIBILITY_ICON_MAP: Record<number, IconProps["source"]> = {
  0: LockIcon,    // PRIVATE
  1: ViewIcon,    // PUBLIC
  10: LinkIcon,   // UNLISTED
  11: LockIcon,   // DRAFT
};

export interface EventMetadata {
  visibility: Visibility[];
  ticketStatus: TicketStatus[];
  eventStatus: EventStatus[];
  formats: Format[];
  categories: EventCategory[];
  expertiseLevels: ExpertiseLevel[];
  instructorTitles: InstructorTitle[];
  paymentPayout: PaymentPayout[];
  paymentProcessedPlatform: PaymentProcessedPlatform[];
  brandRole: BrandRole[];
  socialProfiles: SocialProfileConfig[];
  privateNoteTone: PrivateNoteTone[];
  eventRelations: EventRelationType[];
  instructorProfileURL: string;
}

// Standard option format for dropdowns/selects
export interface SelectOption {
  label: string;
  value: string;
}

// Extended option format with description
export interface SelectOptionWithDescription extends SelectOption {
  description: string;
}

// Extended option format with description and icon
export interface SelectOptionWithDescriptionAndIcon extends SelectOptionWithDescription {
  icon: IconProps["source"];
}

type UseEventMetadataResult = {
  metadata: EventMetadata | undefined;
  visibilityOptions: SelectOptionWithDescriptionAndIcon[];
  categoryOptions: SelectOption[];
  formatOptions: SelectOption[];
  expertiseLevelOptions: SelectOption[];
  instructorTitleOptions: SelectOption[];
  socialProfileOptions: SelectOption[];
  eventStatusOptions: SelectOptionWithDescription[];
  ticketStatusOptions: SelectOptionWithDescription[];
  brandRoleOptions: SelectOptionWithDescription[];
  eventRelationOptions: SelectOptionWithDescription[];
  paymentPayoutOptions: SelectOptionWithDescription[];
  paymentPlatformOptions: SelectOptionWithDescription[];
  toneOptions: Array<{ value: NoteTone; label: string; description: string }>;
  isLoading: boolean;
  error: Error | null;
};

/**
 * Hook to fetch event metadata and provide pre-formatted options for dropdowns.
 * Metadata is cached for 1 hour since it doesn't change often.
 * 
 * Note: This is in-memory cache (per session). Data is cleared on page refresh.
 */
const useEventMetadata = (): UseEventMetadataResult => {
  const { GET } = useApiRequest();

  const {
    data: metadata,
    isLoading,
    error,
  } = useQuery<EventMetadata>({
    queryKey: ["eventMetadata"],
    queryFn: () => GET("/api/metadata/events"),
    // Event metadata is static - cache indefinitely (in-memory)
    staleTime: Infinity,
    // Keep in memory for 30 days even when not actively used (garbage collection)
    gcTime: 30 * 24 * 60 * 60 * 1000, // 30 days
  });

  // Pre-formatted options - ready to use in components
  const visibilityOptions = useMemo<SelectOptionWithDescriptionAndIcon[]>(
    () =>
      metadata?.visibility?.map((v) => ({
        label: v.label,
        value: v.id.toString(),
        description: v.description,
        icon: VISIBILITY_ICON_MAP[v.id] || VISIBILITY_ICON_MAP[0],
      })) || [],
    [metadata?.visibility]
  );

  const categoryOptions = useMemo<SelectOption[]>(
    () =>
      metadata?.categories?.map((c) => ({
        label: c.name,
        value: c.id,
      })) || [],
    [metadata?.categories]
  );

  const formatOptions = useMemo<SelectOption[]>(
    () =>
      metadata?.formats?.map((f) => ({
        label: f.label,
        value: f.id,
      })) || [],
    [metadata?.formats]
  );

  const expertiseLevelOptions = useMemo<SelectOption[]>(
    () =>
      metadata?.expertiseLevels?.map((e) => ({
        label: e.label,
        value: e.id,
      })) || [],
    [metadata?.expertiseLevels]
  );

  const instructorTitleOptions = useMemo<SelectOption[]>(
    () =>
      metadata?.instructorTitles?.map((t) => ({
        label: t.label,
        value: t.slug,
      })) || [],
    [metadata?.instructorTitles]
  );

  const socialProfileOptions = useMemo<SelectOption[]>(
    () =>
      metadata?.socialProfiles?.map((s) => ({
        label: s.label,
        value: s.slug,
      })) || [],
    [metadata?.socialProfiles]
  );

  const eventStatusOptions = useMemo<SelectOptionWithDescription[]>(
    () =>
      metadata?.eventStatus?.map((e) => ({
        label: e.label,
        value: e.slug,
        description: e.description,
      })) || [],
    [metadata?.eventStatus]
  );

  const ticketStatusOptions = useMemo<SelectOptionWithDescription[]>(
    () =>
      metadata?.ticketStatus?.map((t) => ({
        label: t.label,
        value: t.slug,
        description: t.description,
      })) || [],
    [metadata?.ticketStatus]
  );

  const brandRoleOptions = useMemo<SelectOptionWithDescription[]>(
    () =>
      metadata?.brandRole?.map((b) => ({
        label: b.label,
        value: b.slug,
        description: b.description,
      })) || [],
    [metadata?.brandRole]
  );

  const eventRelationOptions = useMemo<SelectOptionWithDescription[]>(
    () =>
      metadata?.eventRelations?.map((r) => ({
        label: r.label,
        value: r.slug,
        description: r.description,
      })) || [],
    [metadata?.eventRelations]
  );

  const paymentPayoutOptions = useMemo<SelectOptionWithDescription[]>(
    () =>
      metadata?.paymentPayout?.map((p) => ({
        label: p.label,
        value: p.slug,
        description: p.description,
      })) || [],
    [metadata?.paymentPayout]
  );

  const paymentPlatformOptions = useMemo<SelectOptionWithDescription[]>(
    () =>
      metadata?.paymentProcessedPlatform?.map((p) => ({
        label: p.label,
        value: p.slug,
        description: p.description,
      })) || [],
    [metadata?.paymentProcessedPlatform]
  );

  // Note tone options for Notes component
  const toneOptions = useMemo(
    () =>
      metadata?.privateNoteTone?.map((tone) => ({
        value: tone.slug as NoteTone,
        label: tone.label,
        description: tone.description,
      })) || [],
    [metadata?.privateNoteTone]
  );

  return {
    // Raw metadata (for special cases where you need full data)
    metadata,
    
    // Ready-to-use options (no more .map() in components!)
    visibilityOptions,
    categoryOptions,
    formatOptions,
    expertiseLevelOptions,
    instructorTitleOptions,
    socialProfileOptions,
    eventStatusOptions,
    ticketStatusOptions,
    brandRoleOptions,
    eventRelationOptions,
    paymentPayoutOptions,
    paymentPlatformOptions,
    toneOptions,
    
    // Loading and error states
    isLoading,
    error: error ?? null,
  } satisfies UseEventMetadataResult;
};

export default useEventMetadata;
